import { useState } from 'react';
import Modal from '@/components/ui/Modal';
import { pad4 } from '@/pages/talao/utils';
import type { MeuTalao, NumeroTalaoVendedor } from '../types';

interface Props {
  t: MeuTalao;
  aoAbrirVenda: () => void;
  aoCancelarVenda: (numero: number) => Promise<void>;
}

// Monta o número do WhatsApp com o DDI 55, sem duplicar quando já vem pronto.
function waNumero(telefone: string | null): string {
  const d = (telefone || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('55') && d.length >= 12 && d.length <= 13) return d;
  return `55${d}`;
}

export default function GradeNumeros({ t, aoAbrirVenda, aoCancelarVenda }: Props) {
  const [vendido, setVendido] = useState<NumeroTalaoVendedor | null>(null);
  const [esperando, setEsperando] = useState<NumeroTalaoVendedor | null>(null);
  const [cancelando, setCancelando] = useState(false);

  const numeros = t.numeros || [];
  const inicio = numeros.length > 0 ? numeros[0].numero : null;
  const fim = numeros.length > 0 ? numeros[numeros.length - 1].numero : null;

  function tocar(n: NumeroTalaoVendedor) {
    if (n.status === 'paga') setVendido(n);
    else if (n.status === 'reservada') setEsperando(n);
    else aoAbrirVenda();
  }

  async function cancelarEsperando() {
    if (!esperando) return;
    setCancelando(true);
    try {
      await aoCancelarVenda(esperando.numero);
    } finally {
      setCancelando(false);
      setEsperando(null);
    }
  }

  function reenviarWhats(n: NumeroTalaoVendedor) {
    const numero = waNumero(n.telefone);
    if (!numero) return;
    const nome = n.comprador ? n.comprador.split(/\s+/)[0] : 'você';
    const texto = `Olá, ${nome}! Seu número da ação entre amigos é ${pad4(n.numero)}.`;
    window.open(
      `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  return (
    <div>
      {/* Faixa de números */}
      {inicio != null && fim != null && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-foreground-900">Seus números</p>
          <p className="text-sm font-semibold text-secondary-500">
            {pad4(inicio)} – {pad4(fim)}
          </p>
        </div>
      )}

      {/* Grade de chips 56×56, 5 por linha */}
      <div className="mt-4 grid grid-cols-5 gap-2 lg:grid-cols-8">
        {numeros.map((n) => {
          if (n.status === 'paga') {
            return (
              <button
                key={n.numero}
                type="button"
                onClick={() => tocar(n)}
                className="flex h-14 cursor-pointer flex-col items-center justify-center rounded-[14px] border border-success-300 bg-success-50 text-foreground-900 transition-colors hover:bg-success-100"
              >
                <span className="text-[15px] font-bold leading-none">{pad4(n.numero)}</span>
                <span className="mt-1 flex h-3 w-3 items-center justify-center text-success-600">
                  <i className="ri-check-line text-xs" />
                </span>
              </button>
            );
          }
          if (n.status === 'reservada') {
            return (
              <button
                key={n.numero}
                type="button"
                onClick={() => tocar(n)}
                className="flex h-14 cursor-pointer flex-col items-center justify-center rounded-[14px] border border-warning-300 bg-warning-50 text-foreground-900 transition-colors hover:bg-warning-100"
              >
                <span className="text-[15px] font-bold leading-none">{pad4(n.numero)}</span>
                <span className="mt-1 flex h-3 w-3 items-center justify-center text-warning-600">
                  <i className="ri-time-line text-xs" />
                </span>
              </button>
            );
          }
          return (
            <button
              key={n.numero}
              type="button"
              onClick={() => tocar(n)}
              className="flex h-14 cursor-pointer items-center justify-center rounded-[14px] border border-secondary-200 bg-white text-[15px] font-bold text-secondary-500 transition-colors hover:bg-background-100"
            >
              {pad4(n.numero)}
            </button>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-secondary-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-success-500" /> vendido
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-warning-500" /> esperando Pix
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border border-secondary-300 bg-white" /> livre
        </span>
      </div>

      {/* Folha: número vendido */}
      <Modal aberto={vendido != null} titulo={`Número ${pad4(vendido?.numero ?? 0)}`} aoFechar={() => setVendido(null)}>
        {vendido && (
          <div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">Comprador</span>
                <span className="font-semibold text-foreground-900">{vendido.comprador || '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">WhatsApp</span>
                <span className="font-semibold text-foreground-900">{vendido.telefone || '—'}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => reenviarWhats(vendido)}
              disabled={!waNumero(vendido.telefone)}
              className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-whatsapp-line" />
              </span>
              Reenviar os números no WhatsApp
            </button>
          </div>
        )}
      </Modal>

      {/* Folha: número esperando Pix */}
      <Modal aberto={esperando != null} titulo={`Número ${pad4(esperando?.numero ?? 0)}`} aoFechar={() => setEsperando(null)}>
        {esperando && (
          <div>
            <div className="flex items-center gap-3 rounded-2xl bg-warning-50 px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning-100 text-warning-600">
                <i className="ri-time-line text-lg" />
              </span>
              <p className="text-sm text-warning-800">Este número está esperando o Pix.</p>
            </div>

            <button
              type="button"
              onClick={cancelarEsperando}
              disabled={cancelando}
              className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-6 py-3.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {cancelando ? (
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-loader-4-line animate-spin" />
                </span>
              ) : null}
              Cancelar venda
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}